import { useInfiniteQuery, useMutation, useQueryClient, useQuery, } from '@tanstack/react-query';
import { z } from 'zod';
import { taskCreateSchema, taskSchema, } from '../types/entities/Task.js';
import { dueDateForSchedule } from '../utilities/taskSchedule.utils.js';
// Response schemas
const taskResponseSchema = z.array(taskSchema);
const createTaskResponseSchema = z.object({
    taskId: z.string(),
});
const taskPageResponseSchema = z.object({
    items: z.array(taskSchema),
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    hasMore: z.boolean(),
});
const projectTaskStatsResponseSchema = z.array(z.object({
    projectId: z.string(),
    total: z.number(),
    completed: z.number(),
    inProgress: z.number(),
    pending: z.number(),
}));
const taskGroupsResponseSchema = z.array(z.object({ key: z.string().nullable(), count: z.number() }));
/**
 * The query keys of every cache that holds tasks. All of them start with 'tasks', so invalidating
 * `taskKeys.all` - after a task, label, goal or plan item changes - refetches every one.
 */
export const taskKeys = {
    all: ['tasks'],
    /** Every run of task pages */
    pagedAll: ['tasks', 'paged'],
    paged: (params) => ['tasks', 'paged', params],
    /** The tasks linked to any goal, which goal progress is worked out from */
    goalLinked: ['tasks', 'goal-linked'],
    detailAll: ['tasks', 'detail'],
    detail: (taskId) => ['tasks', 'detail', taskId],
    projectStats: ['tasks', 'project-stats'],
    /** The group counts of a grouped list - refetched, never patched */
    groups: (params) => ['tasks', 'groups', params],
};
/** How many tasks a page loads unless the caller asks for another size. */
const TASK_PAGE_SIZE = 25;
/** Fills in the fields the server may leave out of an older task. */
const withTaskDefaults = (task) => ({
    ...task,
    isCommitted: task.isCommitted ?? false,
    recurrence: {
        ...task.recurrence,
        disabled: task.recurrence?.disabled ?? false,
    },
});
const changeIn = (tasks, taskId, change) => tasks.flatMap((task) => {
    if (task.id !== taskId)
        return [task];
    const changed = change(task);
    return changed ? [changed] : [];
});
/**
 * Applies a change to one task in every cache that holds it - the loaded task pages, the
 * goal-linked tasks and the task's own - so the change shows before the refetch confirms it. Each
 * family of keys holds tasks in its own shape, so each is patched by its key, never by guessing
 * at the data; project counts are only ever refetched.
 */
export const patchTaskCaches = (queryClient, taskId, change) => {
    queryClient.setQueriesData({ queryKey: taskKeys.pagedAll }, (data) => data
        ? {
            ...data,
            pages: data.pages.map((page) => ({
                ...page,
                items: changeIn(page.items, taskId, change),
            })),
        }
        : data);
    queryClient.setQueryData(taskKeys.goalLinked, (tasks) => tasks ? changeIn(tasks, taskId, change) : tasks);
    queryClient.setQueryData(taskKeys.detail(taskId), (task) => task ? (change(task) ?? undefined) : task);
};
/** The task as any loaded cache holds it, to show at once while its own request is in flight. */
export const findCachedTask = (queryClient, taskId) => {
    const own = queryClient.getQueryData(taskKeys.detail(taskId));
    if (own)
        return own;
    for (const [, data] of queryClient.getQueriesData({
        queryKey: taskKeys.pagedAll,
    })) {
        const found = data?.pages.flatMap((page) => page.items).find((task) => task.id === taskId);
        if (found)
            return found;
    }
    return queryClient.getQueryData(taskKeys.goalLinked)?.find((task) => task.id === taskId);
};
/**
 * The tasks of a run of pages in order, each once: an optimistic removal shifts the server's
 * offsets, so the next page can repeat a task the one before already holds.
 */
export const taskPageItems = (data) => {
    const seen = new Set();
    return (data?.pages ?? []).flatMap((page) => page.items.filter((task) => {
        if (seen.has(task.id))
            return false;
        seen.add(task.id);
        return true;
    }));
};
/**
 * The user's tasks a page at a time, filtered and sorted on the server: a whole list for infinite
 * scrolling, or one dashboard section with "Load More". Call `fetchNextPage` for the next page,
 * and join the pages with `taskPageItems`. Pass `enabled: false` to hold a list not on screen.
 */
export const useGetTasksPage = (authenticatedRequest, params, { enabled = true } = {}) => useInfiniteQuery({
    queryKey: taskKeys.paged(params),
    initialPageParam: 1,
    enabled,
    queryFn: async ({ pageParam }) => {
        const list = (values) => values.length > 0 ? values.join(',') : undefined;
        const limit = params.limit ?? TASK_PAGE_SIZE;
        const response = await authenticatedRequest({
            url: '/tasks/paged',
            method: 'GET',
            params: {
                page: pageParam,
                limit,
                sortBy: params.sortBy,
                includeCompleted: params.includeCompleted,
                labelIds: list(params.labelIds),
                priorities: list(params.priorities),
                projectIds: list(params.projectIds),
                section: params.section,
                today: params.today,
                groupBy: params.group?.by,
                groupKey: params.group ? (params.group.key ?? 'none') : undefined,
            },
        }, taskPageResponseSchema);
        const data = response?.data;
        return {
            items: (data?.items ?? []).map(withTaskDefaults),
            page: data?.page ?? pageParam,
            limit: data?.limit ?? limit,
            total: data?.total ?? 0,
            hasMore: data?.hasMore ?? false,
        };
    },
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
});
/**
 * One task by id. Shows the task as any loaded list already holds it while its own request is in
 * flight, so opening a task from a list needs no spinner.
 */
export const useGetTask = (authenticatedRequest, taskId) => {
    const queryClient = useQueryClient();
    return useQuery({
        queryKey: taskKeys.detail(taskId),
        queryFn: async () => {
            const response = await authenticatedRequest({ url: `/tasks/${taskId}`, method: 'GET' }, taskSchema);
            if (!response?.data)
                throw new Error('Task not found');
            return withTaskDefaults(response.data);
        },
        placeholderData: () => findCachedTask(queryClient, taskId),
        // A task that does not exist will not start existing on a retry
        retry: false,
    });
};
/**
 * Every task linked to one of the user's goals - all that goal progress, What's Next and a plan's
 * items need. Lookups go by plan item and task id, so one list serves every goal.
 */
export const useGetGoalLinkedTasks = (authenticatedRequest) => useQuery({
    queryKey: taskKeys.goalLinked,
    queryFn: async () => {
        const response = await authenticatedRequest({ url: '/tasks', method: 'GET', params: { linkedToGoal: true } }, taskResponseSchema);
        return (response?.data ?? []).map(withTaskDefaults);
    },
});
/**
 * Every group of a grouped list - each label, priority or project its tasks fall in - with how
 * many tasks are in each, counted on the server across all of them, not just those loaded. Each
 * group's tasks are paged with `useGetTasksPage`, its `group` set.
 */
export const useGetTaskGroups = (authenticatedRequest, params, { enabled = true } = {}) => useQuery({
    queryKey: taskKeys.groups(params),
    enabled,
    queryFn: async () => {
        const list = (values) => values.length > 0 ? values.join(',') : undefined;
        const response = await authenticatedRequest({
            url: '/tasks/groups',
            method: 'GET',
            params: {
                groupBy: params.groupBy,
                includeCompleted: params.includeCompleted,
                labelIds: list(params.labelIds),
                priorities: list(params.priorities),
                projectIds: list(params.projectIds),
                section: params.section,
                today: params.today,
            },
        }, taskGroupsResponseSchema);
        return response?.data ?? [];
    },
});
/** Completed, in-progress and pending task counts for each of the user's projects. */
export const useGetProjectTaskStats = (authenticatedRequest) => useQuery({
    queryKey: taskKeys.projectStats,
    queryFn: async () => {
        const response = await authenticatedRequest({ url: '/tasks/project-stats', method: 'GET' }, projectTaskStatsResponseSchema);
        return response?.data ?? [];
    },
});
export const useCreateTask = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => {
            const validatedData = taskCreateSchema.parse(data);
            return authenticatedRequest({
                url: '/tasks',
                method: 'POST',
                data: validatedData,
            }, createTaskResponseSchema);
        },
        // Not shown ahead of the server: which section and page a new task lands in is the server's
        // to say, so every task list is refetched
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: taskKeys.all });
            // A task created for a goal or milestone is a new plan item of it
            if (variables.goal) {
                queryClient.invalidateQueries({ queryKey: ['goalRefinement'] });
                queryClient.invalidateQueries({ queryKey: ['goalRefinements'] });
            }
        },
    });
};
export const useUpdateTask = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ taskId, data }) => {
            const validatedData = taskSchema.partial().parse(data);
            return authenticatedRequest({
                url: `/tasks/${taskId}`,
                method: 'PUT',
                data: validatedData,
            }, z.object({ message: z.string() }));
        },
        onMutate: async ({ taskId, data }) => {
            // So a refetch already in flight cannot overwrite the change shown below
            await queryClient.cancelQueries({ queryKey: taskKeys.all });
            // The task as it was, to put back if the update fails
            const originalTask = findCachedTask(queryClient, taskId);
            // Show the change in every cache holding the task. A due date left out of the update is
            // kept unless the task now starts after it, as on the server, so the lists do not jump
            // until the refetch.
            patchTaskCaches(queryClient, taskId, (task) => ({
                ...task,
                ...data,
                dueDate: data.dueDate !== undefined
                    ? data.dueDate
                    : data.when !== undefined
                        ? dueDateForSchedule(data.when, task.dueDate)
                        : task.dueDate,
                updatedAt: new Date(),
            }));
            return { originalTask };
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: taskKeys.all });
        },
        onError: (error, variables, context) => {
            // Put back only this task, not whole snapshots, so a later change to another task - or to
            // this one, made while this request was in flight - is not undone with it
            const originalTask = context?.originalTask;
            if (originalTask)
                patchTaskCaches(queryClient, variables.taskId, () => originalTask);
            queryClient.invalidateQueries({ queryKey: taskKeys.all });
        },
    });
};
/**
 * "Start now" for a goal plan task waiting on its prerequisites: it stops waiting, and moves to
 * today (or its next rule day) if its date has passed. Refetches the tasks once it is done.
 */
export const useUnblockTask = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (taskId) => authenticatedRequest({ url: `/tasks/${taskId}/unblock`, method: 'POST' }, z.object({ message: z.string() })),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: taskKeys.all }),
    });
};
export const useRemoveTask = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (taskId) => {
            return authenticatedRequest({
                url: `/tasks/${taskId}`,
                method: 'DELETE',
            }, z.object({
                success: z.boolean(),
                message: z.string().optional(),
            }));
        },
        onMutate: async (taskId) => {
            // So a refetch already in flight cannot put the task back
            await queryClient.cancelQueries({ queryKey: taskKeys.all });
            // Take it out of every list holding it
            patchTaskCaches(queryClient, taskId, () => null);
        },
        onSuccess: (response, taskId) => {
            // The task's own query is dropped, not refetched: it would only find the task gone
            queryClient.removeQueries({ queryKey: taskKeys.detail(taskId), exact: true });
            queryClient.invalidateQueries({
                queryKey: taskKeys.all,
                predicate: (query) => !(query.queryKey[1] === taskKeys.detailAll[1] && query.queryKey[2] === taskId),
            });
        },
        onError: () => {
            // The task is still there: refetch the lists it was taken out of
            queryClient.invalidateQueries({ queryKey: taskKeys.all });
        },
    });
};
